import React from 'react'
import {
  makeStyles,
  Collapse,
  FilterChipGroup,
  Grid,
  InputLabel,
  Paper,
  useClientOptions,
  useUser
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import type { InFilter, RangeFilter, ValueFilter } from 'json-to-lucene'
import { useSearchUsersQuery } from '@paintscout/api'
import { removeAuth0Prefix } from '@paintscout/util'
import { formatName, getLeadSources, getQuoteTags } from '@paintscout/util/builder'
import moment from 'moment'
import classNames from 'classnames'
import { mapSourceToOptions, preferredContactMethodOptions } from './ContactSearchFilters'

const useStyles = makeStyles<Theme, ContactSearchFilterChipsProps>(
  (theme) => ({
    root: {
      width: '100%'
    },
    paper: {
      padding: theme.spacing(4, 6),
      marginBottom: theme.spacing(1)
    },
    chipGroup: {
      margin: theme.spacing(1, 0),
      gridTemplateColumns: `repeat(auto-fill, minmax(120px, 1fr))`
    },
    longChip: {
      gridTemplateColumns: `1fr`
    }
  }),
  { name: 'ContactSearchFilterChips' }
)

export interface ContactSearchFilterChipsProps {
  classes?: StyleClasses<typeof useStyles>
  show: boolean
  filters: ValueFilter[]
  onDelete: (args: { filter: ValueFilter; valueIndex?: number | string; valueLabel?: string }) => void
}

function ContactSearchFilterChips(props: ContactSearchFilterChipsProps) {
  const { show, filters, onDelete } = props
  const { options, clientId } = useClientOptions()
  const { isSuperadmin } = useUser()
  const classes = useStyles(props)

  const query = isSuperadmin ? `app_metadata.companyId:"${clientId}" AND app_metadata.active:true` : ''
  const { data, loading: usersLoading } = useSearchUsersQuery({
    variables: {
      query
    },
    fetchPolicy: 'cache-and-network'
  })

  const dateFormat = 'MMM D, YYYY'
  const estimatorFilter = filters.find((filter) => filter.id === 'owner.id') as InFilter
  const estimatorOptions = (data?.searchUsers?.rows || []).map((user) => ({
    label: user.name || (user.user_metadata ? formatName(user.user_metadata) : ''),
    value: removeAuth0Prefix(user.user_id)
  }))
  const activeEstimatorOptions = (estimatorFilter?.value ?? []).map((value) =>
    estimatorOptions.find((option) => option.value === value)
  )

  const dateFilter = filters.find((filter) => filter.id === 'created_date') as RangeFilter

  const quoteTagOptions = getQuoteTags({ options })
  const tagsFilter = filters.find((filter) => filter.id === 'tags') as InFilter
  const activeTagsOptions = (tagsFilter?.value ?? []).map((value) =>
    quoteTagOptions.find((option) => option.name === value)
  )
  const contactMethodFilter = filters.find((filter) => filter.id === 'preferred_contact_method') as InFilter
  const activeContactMethodOptions = preferredContactMethodOptions.filter((option) => {
    if (option.value === '') {
      return false
    }
    return contactMethodFilter?.value?.includes(option.value)
  })

  const sourceFilter = filters.find((filter) => filter.id === 'lead_source') as InFilter
  const sourceOptions = getLeadSources({ options, type: 'leadSources' })
  const activeSourceOptions = mapSourceToOptions(sourceOptions).filter((option) => {
    return sourceFilter?.value?.includes(option.value)
  })

  const getDateFilterChipValue = () => {
    if (!dateFilter?.value) {
      return ''
    }
    if (dateFilter.value[0] && !dateFilter.value[1]) {
      return `After ${moment(dateFilter.value[0]).format(dateFormat)}`
    }
    if (!dateFilter.value[0] && dateFilter.value[1]) {
      return `Before ${moment(dateFilter.value[1]).format(dateFormat)}`
    }
    return `${moment(dateFilter.value[0]).format(dateFormat)} - ${moment(dateFilter.value[1]).format(dateFormat)}`
  }

  return (
    <div className={classes.root}>
      <Collapse show={show}>
        <Paper className={classNames(classes.root, classes.paper)}>
          <Grid container spacing={5}>
            {!!activeEstimatorOptions?.length && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Owner</InputLabel>
                <FilterChipGroup
                  loading={usersLoading}
                  classes={{
                    root: classNames(classes.chipGroup, activeEstimatorOptions.length === 1 ? classes.longChip : null)
                  }}
                  chips={activeEstimatorOptions.map((option, index) => ({
                    label: option?.label,
                    onDelete: () => onDelete({ filter: estimatorFilter, valueIndex: index })
                  }))}
                />
              </Grid>
            )}
            {!!dateFilter?.value && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Created Date</InputLabel>
                <FilterChipGroup
                  classes={{ root: classNames(classes.chipGroup, classes.longChip) }}
                  chips={[
                    {
                      label: getDateFilterChipValue(),
                      onDelete: () => onDelete({ filter: dateFilter, valueIndex: 0 })
                    }
                  ]}
                />
              </Grid>
            )}
            {!!activeSourceOptions?.length && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Source{activeSourceOptions.length > 1 ? 's' : ''}</InputLabel>
                <FilterChipGroup
                  classes={{ root: classNames(classes.chipGroup, classes.longChip) }}
                  chips={activeSourceOptions.map((option, _index) => ({
                    label: option.label,
                    onDelete: () => onDelete({ filter: sourceFilter, valueIndex: option.key, valueLabel: option.label })
                  }))}
                />
              </Grid>
            )}
            {!!activeContactMethodOptions.length && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Contact Method{activeContactMethodOptions.length > 1 ? 's' : ''}</InputLabel>
                <FilterChipGroup
                  classes={{ root: classNames(classes.chipGroup, classes.longChip) }}
                  chips={activeContactMethodOptions.map((option, index) => ({
                    label: option.label,
                    onDelete: () => onDelete({ filter: contactMethodFilter, valueIndex: index })
                  }))}
                />
              </Grid>
            )}
            {!!activeTagsOptions?.length && (
              <Grid item xs={12} md={6} lg={3}>
                <InputLabel>Tags</InputLabel>
                <FilterChipGroup
                  classes={{
                    root: classNames(classes.chipGroup, activeTagsOptions.length === 1 ? classes.longChip : null)
                  }}
                  chips={activeTagsOptions.map((option, index) => ({
                    label: option.label,
                    color: option.color,
                    onDelete: () => onDelete({ filter: tagsFilter, valueIndex: index })
                  }))}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </Collapse>
    </div>
  )
}

export default ContactSearchFilterChips
